import { useEffect, useState } from "react";
import { ExternalLink, FileText, X } from "lucide-react";
import { Button } from "@/design-system/primitives";
import { apiBlob } from "@/lib/api/client";
import type { ClinicianGuide } from "@/types/clinician";
import { MessageResourcePicker } from "./MessageResourcePicker";

function pharmacyLabel(key: string) {
  return (
    {
      precisionmeds: "Precision Medicine",
      the_pharmacy_hub: "The Pharmacy Hub / Olympia",
      optiorx: "OptioRx",
    }[key] ?? key.replaceAll("_", " ")
  );
}

export function GuideAttachmentPicker({
  guides,
  value,
  onChange,
  disabled,
  loading,
  failed,
  onRetry,
  productId,
}: {
  guides: ClinicianGuide[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  loading?: boolean;
  failed?: boolean;
  onRetry?: () => void;
  productId?: string | null;
}) {
  // Keep unfinished, archived, and raw-material documents out of patient replies.
  const available = guides.filter(
    (guide) =>
      guide.active &&
      guide.status === "ready" &&
      guide.coa_scope !== "raw_material",
  );
  const attached = available.find((guide) => guide.id === value);
  return (
    <>
      <MessageResourcePicker
        label="Attach PDF"
        icon={<FileText size={16} aria-hidden />}
        title="Choose a PDF"
        disabled={disabled}
        loading={loading}
        failed={failed}
        onRetry={onRetry}
        currentId={value}
        options={available.map((guide) => ({
          id: guide.id,
          title: guide.title,
          category:
            guide.document_kind === "coa"
              ? "Certificate of Analysis"
              : "Patient guide",
          description: [
            productId && guide.product_ids.includes(productId)
              ? "Recommended for this product"
              : "",
            guide.partner_keys.map(pharmacyLabel).join(" · "),
            guide.lot_number ? `Lot ${guide.lot_number}` : "",
            guide.description,
          ]
            .filter(Boolean)
            .join(" · "),
          searchText: [
            guide.filename,
            ...guide.product_ids,
            ...guide.match_terms,
          ].join(" "),
        }))}
        renderPreview={(id) => (
          <PdfPreview guide={available.find((guide) => guide.id === id)!} />
        )}
        onSelect={onChange}
      />
      {attached && (
        <div className="order-last flex basis-full items-center gap-8 border border-line-subtle bg-page pl-12 text-14">
          <FileText size={16} className="shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 break-words">
            {attached.title}{" "}
            <span className="text-12 text-ink-secondary">· PDF attached</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            className="h-44 w-44 shrink-0 p-0"
            aria-label="Remove attached PDF"
            disabled={disabled}
            onClick={() => onChange("")}
          >
            <X size={16} aria-hidden />
          </Button>
        </div>
      )}
    </>
  );
}

function PdfPreview({ guide }: { guide: ClinicianGuide }) {
  const [url, setUrl] = useState<string | null>(guide.view_url);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | undefined;
    setFailed(false);
    setUrl(guide.view_url);
    if (!guide.view_url) {
      if (!guide.content_url) setFailed(true);
      else
        void apiBlob(guide.content_url)
          .then((blob) => {
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            setUrl(objectUrl);
          })
          .catch(() => {
            if (!cancelled) setFailed(true);
          });
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [guide.view_url, guide.content_url, attempt]);

  return (
    <div className="flex flex-col gap-15 p-20">
      <header className="flex flex-col gap-8">
        <p className="type-label-xs text-ink-secondary">PDF preview</p>
        <h3 className="text-24 leading-120">{guide.title}</h3>
        {guide.description && (
          <p className="text-14 leading-150 text-ink-secondary">
            {guide.description}
          </p>
        )}
        {guide.document_kind === "coa" && (
          <div className="border border-line-subtle bg-raised p-12 text-14 leading-150">
            <p>
              {guide.partner_keys.map(pharmacyLabel).join(" · ") ||
                "Pharmacy not specified"}
              {guide.lot_number ? ` · Lot ${guide.lot_number}` : ""}
            </p>
            <p className="mt-5 text-ink-secondary">
              Confirm the pharmacy and product match the patient's order before
              sharing.
            </p>
          </div>
        )}
        <p className="break-words text-12 text-ink-secondary">
          {guide.filename}
        </p>
      </header>
      {failed ? (
        <div role="alert" className="border border-line-subtle p-15 text-14">
          <p>The PDF preview could not be loaded.</p>
          <Button
            type="button"
            variant="ghost"
            className="mt-8 min-h-44"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry preview
          </Button>
        </div>
      ) : url ? (
        <>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-44 items-center gap-8 self-start text-14 underline focus-visible:shadow-focus"
          >
            Open PDF in new tab <ExternalLink size={14} aria-hidden />
          </a>
          <iframe
            title={`PDF preview: ${guide.title}`}
            src={`${url}#toolbar=1&view=FitH`}
            className="h-[48rem] w-full border border-line-subtle bg-sunken"
          />
          <p className="text-12 text-ink-secondary">
            If the embedded preview is unavailable, open the PDF in a new tab.
          </p>
        </>
      ) : (
        <div role="status" className="h-240 animate-pulse bg-sunken">
          <span className="sr-only">Fetching PDF preview…</span>
        </div>
      )}
    </div>
  );
}
